import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import { EventOutlined } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';
import { Appointment, AppointmentRecurring } from '@uplift/api';
import { CalendarDateService } from '@uplift/services';
import { useTimezone } from '@uplift/contexts';


const RecurringChip = withStyles(Chip, theme => ({
  root: {
    backgroundColor: theme.palette.secondary.background,
  }
}));


const useStyles = makeStyles()(theme => ({
  root: {
    padding: theme.spacing('10px', 1),
    display: 'flex',
    alignItems: 'center',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    padding: '5px',
    backgroundColor: theme.palette.secondary.background,
    borderRadius: '50%',
  },
  details: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  title: {
    lineHeight: 1,
  }
}));


export type UpcomingSessionCardProps = {
  session: Pick<Appointment, 'id' | 'fromDatetime' | 'recurring' | 'type'>;
};


export const UpcomingSessionCard: React.VFC<UpcomingSessionCardProps> = (props) => {
  const timezone = useTimezone();
  const l10n = useL10n();
  const { classes } = useStyles();

  const type = React.useMemo(() => l10n.resources.appointment.types[props.session.type], [ l10n, props.session.type ]);
  const recurring = React.useMemo(() => {
    if (!props.session.recurring) {
      return null;
    }

    if (props.session.recurring === AppointmentRecurring.none) {
      return null;
    }

    return l10n.resources.appointment.recurring[props.session.recurring];
  }, [ l10n, props.session.recurring ]);
  const fromDatetime = React.useMemo(() => CalendarDateService.date(props.session.fromDatetime).tz(timezone).format('dddd, MMMM DD'), [ props.session.fromDatetime, timezone ]);

  return (
    <Paper className={classes.root} component="div" variant="outlined">
      <Box className={classes.iconWrapper}>
        <EventOutlined fontSize="inherit" color="secondary" />
      </Box>
      <Box className={classes.details}>
        <Typography component="div" className={classes.title}>
          {type} {recurring && <RecurringChip size="small" label={recurring} />}
        </Typography>
        <Typography variant="body2">{fromDatetime}</Typography>
      </Box>
    </Paper>
  );
};
